import { createRouter } from "@tanstack/react-router";
import { getRequestLazyModules, loadLazyModules } from "src/lib/LazyModules";
import { setActiveRouter } from "src/lib/Navigation";
import { withStatusHeads } from "src/lib/RouteStatusHead";
import { parseSearch, stringifySearch } from "src/lib/SearchParams";
import { NotFoundPage, RouteErrorPage } from "src/routes/-components/RouteBoundaries";
import { routeTree as generatedRouteTree } from "./routeTree.gen";

// Error and not-found pages get their document title from route heads too (src/lib/RouteStatusHead.ts).
const routeTree = withStatusHeads(generatedRouteTree);

export function getRouter() {
  const router = createRouter({
    routeTree,
    parseSearch,
    stringifySearch,
    scrollRestoration: true,
    // The app's Link preloads on hover or focus itself (src/components/common/Link); links rendered
    // some other way don't.
    defaultPreload: false,
    // A navigation waits for the next page's loaders, keeping the current page (and RouteProgress)
    // on screen. No route has a pending component: page loaders wait at most LOADER_CUTOFF_MS and
    // then let the real page render with its own loading states (src/lib/LoaderData.tsx, src/lib/LoaderResult.ts).
    defaultErrorComponent: RouteErrorPage,
    defaultNotFoundComponent: NotFoundPage,
    // Lazy modules the server rendered the page with (src/lib/LazyModules.tsx), loaded before the
    // browser hydrates, so hydration renders them exactly as the server did.
    dehydrate: () => ({ lazyModules: getRequestLazyModules() }),
    hydrate: (dehydrated) => loadLazyModules(dehydrated?.lazyModules),
  });
  setActiveRouter(router);
  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
